import { Injectable } from "@angular/core";
import { MessagingService } from "./shared/services/messaging.service";
import { Subscription } from "rxjs";
import { BUS_MESSAGE_KEY } from "src/app/shared/constants/message-bus";
import { User } from "./shared/constants/ascend-user-info";
import { DELOITTE_USER_DOMAIN, EXTERNAL_USER } from "./shared/constants/external-user-constants";
interface PageInfo {
  applicationName: string;
  pageName: string;
  userID: string;
  buttonName: string;
  languages: string;
  marketSolution: string;
  pageLoadTime: string;
}

interface AnalyticsDataLayer {
  pageInfo: PageInfo;
}

@Injectable({ providedIn: "root" })
export class AdobeAnalyticsService {
  private subscriptions: Subscription[] = [];
  userData: User;
  private w: {
    analyticsDataLayer: AnalyticsDataLayer;

    _satellite: {
      track: (event: string) => {};
    };
  };

  private get pageInfo(): PageInfo {
    return this.w?.analyticsDataLayer && this.w?.analyticsDataLayer.pageInfo;
  }

  // Tracks the start time of the page load process to calculate the total load time.
  private startTime: number = performance.now();

  // A flag to indicate whether all API calls have been completed.
  private allApisLoaded: boolean = false;

  // A flag to indicate whether the UI has been fully rendered.
  private uiRendered: boolean = false;

  /**
   * Initializes tracking by resetting the start time, API load status, and UI render status.
   */
  startTracking = () => {
    this.startTime = performance.now();
    this.resetTracking();
  };

  /**
   * Marks the completion of all API loading processes and triggers a check for overall completion.
   * 
   * This method sets the `allApisLoaded` flag to `true` and invokes the `checkCompletion` method
   * to perform any necessary actions once all APIs have been successfully loaded.
   */
  trackApiEnd = () => {
    this.allApisLoaded = true;
    this.checkCompletion();
  };

  /**
   * Marks the UI as rendered by setting the `uiRendered` property to `true`.
   * Once marked, it triggers a check for completion by calling `checkCompletion`.
   */
  markUIRendered = () => {
    this.uiRendered = true;
    this.checkCompletion();
  };

  /**
   * Checks if all APIs have been loaded and the UI has been rendered.
   * If both conditions are met, this method calculates the time taken to load the page.
   * Convereted ms to s and Trimmed to 2 decimal places
   */
  private checkCompletion = () => {
    if (this.allApisLoaded && this.uiRendered) {
      const timeLapsed = ((performance.now() - this.startTime) / 1000).toFixed(2);
      this.pageInfo.pageLoadTime = timeLapsed + "s";
      this.trackNavigationEvent();
      this.resetTracking();
    }
  };

  resetTracking(){
    this.allApisLoaded = false;
    this.uiRendered = false;
  }

  constructor(private messagingBus: MessagingService) {
    this.w = window as any;
  }

  setUserId(id: string) {
    if (this.pageInfo) {
      this.subscriptions.push(
        this.messagingBus.subscribe(BUS_MESSAGE_KEY.USER_DETAILS, (data) => {
          this.userData = data;
          id = this.userData.userId =
            data?.ssoUser?.userPrincipalName ?? data.userId;
        })
      );
      const [user, domain] = id.split("@");
      this.pageInfo.userID = domain === DELOITTE_USER_DOMAIN ? user : EXTERNAL_USER;
    }
  }

  /**
   * SetLanguageName: method will be used to set language Name
   * 
   * @param {string} language
   */
  setLanguageName = (language: string) => {
    if (this.pageInfo) this.pageInfo.languages = language;
  }

  setPageName(pageName: string) {
    if (this.pageInfo) {
      this.pageInfo.pageName = pageName;
    }
  }
  setApplicationName(applicationName: string) {
    if (this.pageInfo) {
      this.pageInfo.applicationName = applicationName;
    }
  }
  setButtonName(buttonName: string) {
    if (this.pageInfo) {
      this.pageInfo.buttonName = buttonName;
    }
  }

  /**
   * Sets the market solution name in the analytics data layer.
   * 
   * @param {string} marketSolutionName - The name of the market solution to set.
   */
  setMarketSolutionName = (marketSolutionName: string) => {
    if (this.pageInfo) this.pageInfo.marketSolution = marketSolutionName;
  }

  /**
   * Retrieves the market solution name from the page information.
   *
   * @returns {string | undefined} The market solution name, or undefined if not available.
   */
  getMarketSolutionName = () => this.pageInfo?.marketSolution;
  
  trackNavigationEvent () {
    if (this.w && this.w?._satellite && this.w?._satellite.track) {
      this.w._satellite.track("pageView");
    }
  }
  trackNavigationEventAction() {
    if (this.w && this.w?._satellite && this.w?._satellite.track) {
      this.w._satellite.track("buttonClicks");
    }
  }
 
  /**
   * Tracks the "marketSolution" event using Adobe Analytics.
   * 
   * This method checks if the Adobe Analytics satellite object is available
   * and then triggers the "marketSolution" event.
   */
  trackMarketSolutionEvent = async (): Promise<void> => {
    if (this.w && this.w?._satellite && this.w?._satellite.track)
      this.w._satellite.track("marketSolution");
  }

  /**
   * TrackLanguageChangeAction: method will be used to track user language change event
   */
  trackLanguageChangeAction = () => {
    if (this.w && this.w?._satellite && this.w?._satellite.track) 
      this.w._satellite.track("languages");
  }
}
